import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Skeleton } from './components/ui/skeleton';

function AuthGuard({ children }: { children: React.ReactNode }) {
  if (import.meta.env.VITE_AUTH_DISABLED === 'true') return <>{children}</>;
  const token = localStorage.getItem('wa_access_token');
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

const DirectoryPage = lazy(() =>
  import('./features/directory/DirectoryPage').then((m) => ({ default: m.DirectoryPage }))
);
const ChartsPage = lazy(() =>
  import('./features/visualizations/ChartsPage').then((m) => ({ default: m.ChartsPage }))
);
const InsightsPage = lazy(() =>
  import('./features/insights/InsightsPage').then((m) => ({ default: m.InsightsPage }))
);
const AdminPage = lazy(() =>
  import('./features/admin/AdminPage').then((m) => ({ default: m.AdminPage }))
);
const LoginPage = lazy(() =>
  import('./features/login/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const MyProfilePage = lazy(() =>
  import('./features/profile/MyProfilePage').then((m) => ({ default: m.MyProfilePage }))
);

function PageLoader() {
  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-48" />
        <div className="flex-1" />
        <Skeleton className="h-8 w-24" />
      </div>
      <Skeleton className="h-10 w-full rounded-lg" />
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

const pageTransition = { duration: 0.15, ease: 'easeOut' };

function AnimatedPage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
    >
      {children}
    </motion.div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <AuthGuard><App /></AuthGuard>,
    children: [
      { index: true, element: <Navigate to="/members" replace /> },
      {
        path: 'members',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AnimatedPage><DirectoryPage /></AnimatedPage>
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'charts',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AnimatedPage><ChartsPage /></AnimatedPage>
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'insights',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AnimatedPage><InsightsPage /></AnimatedPage>
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'admin',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AnimatedPage><AdminPage /></AnimatedPage>
            </Suspense>
          </ErrorBoundary>
        ),
      },
      {
        path: 'profile',
        element: (
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <AnimatedPage><MyProfilePage /></AnimatedPage>
            </Suspense>
          </ErrorBoundary>
        ),
      },
    ],
  },
]);
